/**
 * Booking attribution (HAL-728).
 *
 * Booking is the highest-intent action on halcy.ai and it emitted nothing
 * before this module existed. The modal path fired no PostHog event at all,
 * and every `BOOKING_URL` render site was a bare `<a target="_blank">`, so
 * anyone who booked from one of them existed only on Nathan's calendar with no
 * route back to the page that produced them.
 *
 * Two mechanisms, because neither one alone reaches every surface:
 *
 * 1. {@link resolveBookingSource} feeds the client-side `booking_lead_submitted`
 *    event, which PostHog can attribute to a session and a person. It only
 *    works where our JS runs.
 * 2. {@link withBookingUtm} tags the calendar URL itself. The visibility-report
 *    email cannot run an `onClick`, so UTM parameters are the ONLY attribution
 *    channel available on the surface that reaches every captured lead.
 */

import { SITE_URL } from '@/lib/metadata';
import type { DiscoveryPageSource } from '@/lib/discovery-source';

// =============================================================================
// Source resolution (client-side event attribution)
// =============================================================================

/**
 * The one source value outside HAL-726's `DISCOVERY_PAGE_SOURCES`.
 *
 * The booking modal is mounted globally, so a booking routinely starts from
 * the homepage – a page that is not one of the four discovery templates.
 * `DISCOVERY_PAGE_SOURCES` stays deliberately un-widened (`InlineVisibilityChecker`
 * indexes its `HEADLINES` map by that union and would break), which is exactly
 * why the event params are typed `source: string`: this extra value rides
 * along without touching the union.
 */
export const HOMEPAGE_SOURCE = 'homepage';

/** Discovery vocabulary as the booking funnel sees it. */
export type BookingSource = DiscoveryPageSource | typeof HOMEPAGE_SOURCE;

/**
 * First URL path segment → discovery source.
 *
 * A table rather than a pass-through because the segment and the source
 * deliberately differ in two cases: `/problems/` reports as `problem` and
 * `/for/` reports as `market`. Those are the strings the four page templates
 * already pass to `InlineVisibilityChecker` / `StickyScrollCTA`, and booking
 * has to match them or the events do not join.
 */
const PATH_SEGMENT_TO_SOURCE: Readonly<Record<string, DiscoveryPageSource>> = {
  learn: 'learn',
  compare: 'compare',
  problems: 'problem',
  for: 'market',
};

/**
 * Resolve the source a booking originated from, given a URL path.
 *
 * Matches on the first path segment so a hub (`/learn`) and a detail page
 * (`/learn/some-article`) report identically.
 *
 * Returns `undefined` for any path outside the known set – omitted rather than
 * defaulted, matching `checkStarted`'s contract, so a missing source reads as
 * "unknown" instead of silently attributing a booking to a real page.
 */
export function resolveBookingSource(pathname: string): BookingSource | undefined {
  if (!pathname.startsWith('/')) return undefined;
  const segment = pathname.split('/')[1] ?? '';
  if (segment === '') return HOMEPAGE_SOURCE;
  return PATH_SEGMENT_TO_SOURCE[segment];
}

// =============================================================================
// UTM tagging (link-level attribution)
// =============================================================================

/**
 * `utm_source` for every booking link.
 *
 * Derived from `SITE_URL` rather than written out: the canonical host is
 * `www.halcy.ai` and a hardcoded bare `halcy.ai` would split one attribution
 * dimension into two values that mean the same thing.
 */
const BOOKING_UTM_SOURCE = new URL(SITE_URL).hostname;

/** One campaign covers the whole booking funnel. */
const BOOKING_UTM_CAMPAIGN = 'booking';

/**
 * Every surface that renders a booking link, mapped to its `utm_medium`.
 *
 * The key doubles as `utm_content`, so adding an entry here is the only edit
 * needed to make a new surface distinguishable in analytics. Both halves of
 * the visibility-report email (HTML and plain text) share `report-email`:
 * they are two MIME renderings of one surface, and which part the recipient's
 * client chose is not a fact anyone acts on.
 */
export const BOOKING_SURFACE_MEDIUMS = {
  'report-sidebar': 'web',
  'checker-rate-limit': 'web',
  'report-email': 'email',
} as const satisfies Record<string, 'web' | 'email'>;

/** A surface that renders a booking link. */
export type BookingSurface = keyof typeof BOOKING_SURFACE_MEDIUMS;

/**
 * Append booking UTM parameters to a calendar URL.
 *
 * The separator is chosen from the URL rather than assumed, mirroring
 * `withDiscoverySource`, so a future query string on `BOOKING_URL` does not
 * silently produce a malformed link.
 *
 * Callers rendering into an HTML attribute must still escape the result with
 * `escapeAttr` – this returns raw `&` separators.
 */
export function withBookingUtm(url: string, surface: BookingSurface): string {
  const params = new URLSearchParams({
    utm_source: BOOKING_UTM_SOURCE,
    utm_medium: BOOKING_SURFACE_MEDIUMS[surface],
    utm_campaign: BOOKING_UTM_CAMPAIGN,
    utm_content: surface,
  });
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${params.toString()}`;
}
